import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, Plus, Calculator, ChevronDown, Search, X, Wrench, Upload, FileText, Edit3, Save, Coins, RefreshCw, RotateCcw, Link as LinkIcon, Edit2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/calculations`;

// ─── Helpers ────────────────────────────────────────────────────────
const fmt3 = (n) => (Number.isFinite(+n) ? (+n).toFixed(3) : "—");
const fmt2 = (n) => (Number.isFinite(+n) ? (+n).toFixed(2) : "—");

// Compute the derived Excel columns live so the row updates as the user types.
function derive(row) {
  const L = +row.length || 0, W = +row.width || 0, H = +row.height || 0;
  const PPB = +row.pieces_per_box || 0;
  const PHY = +row.physical_weight || 0;
  const BOXES = +row.boxes || 0;
  const PRICE = +row.unit_price_usd || 0;
  const G = L * W * H;              // Greutate Volumetrică
  const J = PPB * BOXES;            // Bucăți totale
  const K = BOXES * G;              // Total greutate volumetrică
  const Lline = BOXES * PHY;        // Total greutate fizică (name clash with L above)
  const N = PRICE * J;              // Preț total
  return { vol: G, total_pieces: J, total_vol_weight: K, total_phy_weight: Lline, total_price: N };
}

// ─── SKU/Name Autocomplete ─────────────────────────────────────────
function ProductPicker({ value, onSelect, "data-testid": testId }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/products-lookup`, { params: { q } });
        setItems(r.data?.items || []);
      } catch (e) { toast.error("Eroare lookup: " + (e.message || "unknown")); }
      finally { setLoading(false); }
    }, 200);
    return () => clearTimeout(h);
  }, [q, open]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={boxRef} className="relative w-full">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid={testId}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-zinc-800 bg-zinc-900 px-2.5 py-1.5 text-left text-xs text-zinc-100 hover:border-zinc-700">
        <span className={value ? "truncate" : "text-zinc-500"}>
          {value ? `${value.sku} — ${value.name?.slice(0, 40) || ""}` : "Adaugă produs (SKU sau nume)"}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-[420px] rounded-lg border border-zinc-800 bg-zinc-950 shadow-2xl">
          <div className="flex items-center gap-2 border-b border-zinc-800 px-3 py-2">
            <Search className="h-3.5 w-3.5 text-zinc-500" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Caută SKU sau nume..." data-testid="product-picker-search"
              className="w-full bg-transparent text-xs text-zinc-100 placeholder-zinc-600 outline-none" />
            {q && <button onClick={() => setQ("")}><X className="h-3.5 w-3.5 text-zinc-500 hover:text-zinc-200" /></button>}
          </div>
          <div className="max-h-72 overflow-auto">
            {loading && <div className="p-3 text-center text-xs text-zinc-500">Se caută...</div>}
            {!loading && items.length === 0 && <div className="p-3 text-center text-xs text-zinc-500">Niciun rezultat</div>}
            {items.map((it) => (
              <button key={it.sku} onClick={() => { onSelect(it); setOpen(false); }}
                data-testid={`product-picker-item-${it.sku}`}
                className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs hover:bg-zinc-800">
                <div className="min-w-0 flex-1">
                  <div className="font-mono font-semibold text-emerald-300">{it.sku}</div>
                  <div className="truncate text-zinc-400">{it.name}</div>
                </div>
                <div className="shrink-0 text-[10px] text-zinc-500">
                  {it.pieces_per_box > 0 ? `${it.pieces_per_box}/bax` : "—"}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Numeric input cell (yellow-tinted to signal "user-editable") ─
function NumCell({ value, onChange, step = "0.01", "data-testid": testId, min = "0" }) {
  return (
    <input type="number" step={step} min={min} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      data-testid={testId}
      className="w-full rounded-sm border border-blue-800 bg-blue-950/60 px-2.5 py-2 text-right font-mono text-sm text-blue-100 outline-none focus:border-blue-400" />
  );
}

// ─── Row Editor ────────────────────────────────────────────────────
function RowEditor({ row, onSave, onDelete }) {
  const [local, setLocal] = useState(row);
  const [dirty, setDirty] = useState(false);
  const saveTimer = useRef(null);

  useEffect(() => { setLocal(row); setDirty(false); }, [row]);

  const set = (field, value) => {
    setLocal((r) => ({ ...r, [field]: value }));
    setDirty(true);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => flush({ ...local, [field]: value }), 500);
  };
  const flush = async (payload) => {
    try {
      const r = await axios.put(`${API}/weight-transport/${row.id}`, {
        sku: payload.sku,
        length: +payload.length || 0, width: +payload.width || 0, height: +payload.height || 0,
        physical_weight: +payload.physical_weight || 0, boxes: +payload.boxes || 0,
        unit_price_usd: +payload.unit_price_usd || 0,
        pieces_per_box_override: payload.pieces_per_box_override,
      });
      setDirty(false);
      // Propagate the fresh row back to the parent so totals recompute.
      if (r?.data) onSave(r.data);
    } catch (e) { toast.error("Salvare eșuată: " + (e.response?.data?.detail || e.message)); }
  };

  const derived = useMemo(() => derive(local), [local]);

  return (
    <tr className="border-t border-zinc-900 hover:bg-zinc-900/30">
      <td className="px-2 py-2 min-w-[110px] w-[110px]">
        {local.sku ? (
          <div className="flex flex-col gap-1">
            <span data-testid={`row-sku-${row.id}`} className="inline-block w-fit rounded bg-emerald-950/40 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-emerald-300">{local.sku}</span>
            <button onClick={() => flush({ ...local, sku: "", product_name: "", pieces_per_box: null })} className="w-fit text-[9px] text-zinc-500 hover:text-rose-400" title="Schimbă produsul">schimbă</button>
          </div>
        ) : (
          <ProductPicker value={null}
            data-testid={`row-picker-${row.id}`}
            onSelect={(sel) => flush({ ...local, sku: sel.sku,
                                                  product_name: sel.name,
                                                  pieces_per_box: sel.pieces_per_box })} />
        )}
      </td>
      <td className="px-2 py-2 min-w-[220px] w-[220px]" title={local.product_name}>
        <div className="text-sm text-zinc-200 leading-tight break-words">{local.product_name || "—"}</div>
      </td>
      <td className="px-2 py-2 w-[105px]">
        <NumCell value={local.pieces_per_box_override ?? local.pieces_per_box ?? ""}
          onChange={(v) => set("pieces_per_box_override", v === "" ? null : v)}
          step="1" data-testid={`row-ppb-${row.id}`} />
      </td>
      <td className="px-2 py-2 w-[120px]"><NumCell value={local.length} onChange={(v) => set("length", v)} step="0.001" data-testid={`row-length-${row.id}`} /></td>
      <td className="px-2 py-2 w-[120px]"><NumCell value={local.width} onChange={(v) => set("width", v)} step="0.001" data-testid={`row-width-${row.id}`} /></td>
      <td className="px-2 py-2 w-[120px]"><NumCell value={local.height} onChange={(v) => set("height", v)} step="0.001" data-testid={`row-height-${row.id}`} /></td>
      <td className="px-2 py-2 w-[120px] text-right font-mono text-sm text-emerald-300">{fmt3(derived.vol)}</td>
      <td className="px-2 py-2 w-[120px]"><NumCell value={local.physical_weight} onChange={(v) => set("physical_weight", v)} step="0.01" data-testid={`row-phy-${row.id}`} /></td>
      <td className="px-2 py-2 w-[105px]"><NumCell value={local.boxes} onChange={(v) => set("boxes", v)} step="1" data-testid={`row-boxes-${row.id}`} /></td>
      <td className="px-2 py-2 w-[110px] text-right font-mono text-sm text-emerald-300">{derived.total_pieces || "—"}</td>
      <td className="px-2 py-2 w-[120px] text-right font-mono text-sm text-emerald-300">{fmt3(derived.total_vol_weight)}</td>
      <td className="px-2 py-2 w-[120px] text-right font-mono text-sm text-emerald-300">{fmt2(derived.total_phy_weight)}</td>
      <td className="px-2 py-2 w-[120px]"><NumCell value={local.unit_price_usd} onChange={(v) => set("unit_price_usd", v)} step="0.01" data-testid={`row-price-${row.id}`} /></td>
      <td className="px-2 py-2 w-[130px] text-right font-mono text-sm font-semibold text-sky-300">${fmt2(derived.total_price)}</td>
      <td className="px-2 py-2 w-[40px]">
        <button onClick={() => onDelete(row.id)} data-testid={`row-delete-${row.id}`}
          className="rounded p-1.5 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-400">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

// ─── Main Tab ──────────────────────────────────────────────────────
function WeightTransportTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/weight-transport`);
      setRows(r.data?.rows || []);
    } catch (e) { toast.error("Eroare încărcare: " + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addRow = async (sel) => {
    setCreating(true);
    try {
      const r = await axios.post(`${API}/weight-transport`, {
        sku: sel.sku, length: 0, width: 0, height: 0, physical_weight: 0, boxes: 0, unit_price_usd: 0,
      });
      setRows((prev) => [...prev, r.data]);
      toast.success(`Adăugat: ${sel.sku}`);
    } catch (e) { toast.error("Adăugare eșuată: " + (e.response?.data?.detail || e.message)); }
    finally { setCreating(false); }
  };
  const deleteRow = async (id) => {
    if (!window.confirm("Ștergi acest rând?")) return;
    try {
      await axios.delete(`${API}/weight-transport/${id}`);
      setRows((prev) => prev.filter((r) => r.id !== id));
      toast.success("Șters");
    } catch (e) { toast.error("Ștergere eșuată: " + (e.response?.data?.detail || e.message)); }
  };

  // Reset column "Cutii" to 0 on ALL rows in one bulk pass. Other fields
  // (dimensions, physical weight, price, boxes-per-pack) stay untouched.
  // User request 2026-07-28: fill only "Cutii" during a new import cycle.
  const resetBoxes = async () => {
    const rowsWithBoxes = rows.filter((r) => Number(r.boxes || 0) > 0);
    if (rowsWithBoxes.length === 0) {
      toast.info("Nu există rânduri cu cutii > 0. Nimic de resetat.");
      return;
    }
    if (!window.confirm(`Resetezi coloana Cutii la 0 pe toate ${rowsWithBoxes.length} rânduri? (restul câmpurilor rămân neschimbate)`)) return;
    try {
      const results = await Promise.allSettled(
        rowsWithBoxes.map((r) => axios.put(`${API}/weight-transport/${r.id}`, { ...r, boxes: 0 })),
      );
      const okCount = results.filter((x) => x.status === "fulfilled").length;
      const failCount = results.length - okCount;
      // Update local state with the returned rows so derived totals recompute
      setRows((prev) => prev.map((x) => {
        const idx = rowsWithBoxes.findIndex((r) => r.id === x.id);
        if (idx < 0) return x;
        const res = results[idx];
        return res.status === "fulfilled" ? res.value.data : { ...x, boxes: 0 };
      }));
      if (failCount === 0) toast.success(`Reset OK — ${okCount} rânduri`);
      else toast.warning(`Reset parțial — ${okCount} OK, ${failCount} eșuate`);
    } catch (e) {
      toast.error("Reset eșuat: " + (e.response?.data?.detail || e.message));
    }
  };

  // Totals across all rows (bottom sticky footer)
  const totals = useMemo(() => {
    return rows.reduce((acc, r) => {
      const d = derive(r);
      acc.total_pieces += d.total_pieces;
      acc.total_vol_weight += d.total_vol_weight;
      acc.total_phy_weight += d.total_phy_weight;
      acc.total_price += d.total_price;
      return acc;
    }, { total_pieces: 0, total_vol_weight: 0, total_phy_weight: 0, total_price: 0 });
  }, [rows]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 max-w-md">
          <ProductPicker value={null} onSelect={addRow} data-testid="add-product-picker" />
        </div>
        <div className="flex items-center gap-3">
          {rows.length > 0 && (
            <button
              onClick={resetBoxes}
              data-testid="reset-boxes-btn"
              title="Resetează coloana Cutii la 0 pe toate rândurile (restul câmpurilor rămân)"
              className="inline-flex items-center gap-1.5 rounded-md border border-rose-500/40 bg-rose-500/10 px-2.5 py-1.5 text-[11px] font-medium text-rose-200 transition hover:bg-rose-500/20"
            >
              <RotateCcw className="h-3 w-3" />
              Reset Cutii
            </button>
          )}
          <div className="text-[11px] text-zinc-500">
            Câmpurile <span className="rounded bg-amber-500/20 px-1 text-amber-200">galbene</span> sunt editabile;
            restul se calculează automat.
          </div>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-zinc-800 bg-zinc-950/40">
        <table className="w-full text-sm">
          <thead className="bg-zinc-900/70 text-[11px] uppercase tracking-wider text-zinc-400">
            <tr>
              <th className="p-2 text-left">SKU</th>
              <th className="p-2 text-left">Nume</th>
              <th className="p-2 text-right">Buc/bax</th>
              <th className="p-2 text-right">Lungime (m)</th>
              <th className="p-2 text-right">Lățime (m)</th>
              <th className="p-2 text-right">Înălțime (m)</th>
              <th className="p-2 text-right text-emerald-400">G. vol. (m³)</th>
              <th className="p-2 text-right">G. fizică (kg)</th>
              <th className="p-2 text-right">Cutii</th>
              <th className="p-2 text-right text-emerald-400">Buc total</th>
              <th className="p-2 text-right text-emerald-400">Total vol.</th>
              <th className="p-2 text-right text-emerald-400">Total fizic</th>
              <th className="p-2 text-right">Preț $/buc</th>
              <th className="p-2 text-right text-sky-400">Preț total $</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="15" className="p-8 text-center text-zinc-500">Se încarcă...</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="15" className="p-8 text-center text-zinc-500">
                <Plus className="mx-auto mb-2 h-5 w-5" />
                Adaugă primul produs de mai sus.
              </td></tr>
            )}
            {rows.map((r) => (
              <RowEditor key={r.id} row={r}
                onSave={(updated) => setRows((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))}
                onDelete={deleteRow} />
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot className="border-t-2 border-zinc-700 bg-zinc-900/50 text-xs font-semibold">
              <tr>
                <td colSpan="9" className="p-2 text-right text-zinc-400">TOTAL:</td>
                <td className="p-2 text-right font-mono text-emerald-300" data-testid="footer-total-pieces">{totals.total_pieces}</td>
                <td className="p-2 text-right font-mono text-emerald-300" data-testid="footer-total-vol">{fmt3(totals.total_vol_weight)}</td>
                <td className="p-2 text-right font-mono text-emerald-300" data-testid="footer-total-phy">{fmt2(totals.total_phy_weight)}</td>
                <td className="p-2"></td>
                <td className="p-2 text-right font-mono text-sky-300" data-testid="footer-total-price">${fmt2(totals.total_price)}</td>
                <td className="p-2"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {creating && <div className="text-xs text-zinc-500">Se adaugă...</div>}
    </div>
  );
}

// ─── TAB 2: Trimise pentru reparație/înlocuire ────────────────────
const SHIPMENT_STATUS_META = {
  creata:            { label: "Creată",         color: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  trimisa:           { label: "Trimisă",        color: "text-sky-300 border-sky-500/40 bg-sky-500/10" },
  ajuns_in_china:    { label: "Ajuns în China", color: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
};
const ITEM_STATUS_META = {
  trimis:                  { label: "Trimis",              color: "text-amber-300 bg-amber-500/10" },
  receptionat_in_china:    { label: "Recepționat în China", color: "text-sky-300 bg-sky-500/10" },
  trimis_din_china:        { label: "Trimis din China",     color: "text-purple-300 bg-purple-500/10" },
  receptionat:             { label: "Recepționat",          color: "text-emerald-300 bg-emerald-500/10" },
};

function ShipmentItemRow({ shipmentId, item, onUpdate, onDelete }) {
  const [local, setLocal] = useState(item);
  const timer = useRef(null);
  useEffect(() => setLocal(item), [item]);

  const flush = async (payload) => {
    try {
      const r = await axios.put(`${API}/repair-shipments/${shipmentId}/items/${item.item_id}`, {
        sku: payload.sku, quantity: +payload.quantity || 0, status: payload.status,
      });
      onUpdate(r.data);
    } catch (e) { toast.error("Salvare eșuată: " + (e.response?.data?.detail || e.message)); }
  };
  const set = (field, value) => {
    setLocal((r) => ({ ...r, [field]: value }));
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => flush({ ...local, [field]: value }), 400);
  };

  const meta = ITEM_STATUS_META[local.status] || ITEM_STATUS_META.trimis;

  return (
    <tr className="border-b border-zinc-900 text-xs">
      <td className="p-2 font-mono font-semibold text-emerald-300">{local.sku}</td>
      <td className="p-2 truncate text-zinc-400" title={local.product_name}>{local.product_name || "—"}</td>
      <td className="p-2">
        <input type="number" step="1" min="0" value={local.quantity ?? 0}
          onChange={(e) => set("quantity", e.target.value)}
          data-testid={`item-qty-${item.item_id}`}
          className="w-20 rounded border border-amber-500/30 bg-amber-500/5 px-2 py-1 text-right font-mono text-xs text-zinc-100 outline-none focus:border-amber-500/70" />
      </td>
      <td className="p-2">
        <select value={local.status} onChange={(e) => flush({ ...local, status: e.target.value })}
          data-testid={`item-status-${item.item_id}`}
          className={`rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs outline-none ${meta.color.split(" ")[0]}`}>
          {Object.entries(ITEM_STATUS_META).map(([k, m]) => (
            <option key={k} value={k}>{m.label}</option>
          ))}
        </select>
      </td>
      <td className="p-2 text-right">
        <button onClick={() => onDelete(item.item_id)} data-testid={`item-delete-${item.item_id}`}
          className="rounded p-1.5 text-zinc-500 hover:bg-rose-500/10 hover:text-rose-400">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

function ShipmentCard({ shipment, onUpdate, onDelete, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [local, setLocal] = useState(shipment);
  const invoiceRef = useRef(null);
  useEffect(() => setLocal(shipment), [shipment]);

  const meta = SHIPMENT_STATUS_META[local.status] || SHIPMENT_STATUS_META.creata;

  const saveHeader = async () => {
    try {
      const r = await axios.put(`${API}/repair-shipments/${shipment.id}`, {
        date: local.date, transport_price: +local.transport_price || 0,
        awb: local.awb || "", carrier: local.carrier || "",
        status: local.status, notes: local.notes || "",
      });
      onUpdate(r.data);
      setEditing(false);
      toast.success("Salvat");
    } catch (e) { toast.error("Salvare eșuată: " + (e.response?.data?.detail || e.message)); }
  };

  const changeStatus = async (newStatus) => {
    try {
      const r = await axios.put(`${API}/repair-shipments/${shipment.id}`, { status: newStatus });
      onUpdate(r.data);
    } catch (e) { toast.error("Salvare eșuată: " + (e.response?.data?.detail || e.message)); }
  };

  const addItem = async (sel) => {
    try {
      const r = await axios.post(`${API}/repair-shipments/${shipment.id}/items`, {
        sku: sel.sku, quantity: 1, status: "trimis",
      });
      onUpdate(r.data);
    } catch (e) { toast.error("Adăugare eșuată: " + (e.response?.data?.detail || e.message)); }
  };
  const updateItem = (updated) => onUpdate(updated);
  const deleteItem = async (itemId) => {
    if (!window.confirm("Șterg produsul?")) return;
    try {
      await axios.delete(`${API}/repair-shipments/${shipment.id}/items/${itemId}`);
      onRefresh();
    } catch (e) { toast.error("Ștergere eșuată: " + (e.response?.data?.detail || e.message)); }
  };

  const uploadInvoice = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await axios.post(`${API}/repair-shipments/${shipment.id}/invoice`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Factură încărcată");
      onRefresh();
    } catch (e) { toast.error("Upload eșuat: " + (e.response?.data?.detail || e.message)); }
  };
  const invoiceUrl = `${process.env.REACT_APP_BACKEND_URL}/api/calculations/repair-shipments/${shipment.id}/invoice`;

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-950/40" data-testid={`shipment-card-${shipment.id}`}>
      {/* Header */}
      <div className="border-b border-zinc-800 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            {editing ? (
              <>
                <input type="date" value={local.date || ""} onChange={(e) => setLocal({ ...local, date: e.target.value })}
                  className="rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-100" />
                <input placeholder="Firmă transport" value={local.carrier || ""} onChange={(e) => setLocal({ ...local, carrier: e.target.value })}
                  className="rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-100 w-32" />
                <input placeholder="AWB" value={local.awb || ""} onChange={(e) => setLocal({ ...local, awb: e.target.value })}
                  className="rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-100 w-32" />
                <input type="number" step="0.01" placeholder="Preț transport" value={local.transport_price ?? 0}
                  onChange={(e) => setLocal({ ...local, transport_price: e.target.value })}
                  className="rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-100 w-28 text-right" />
              </>
            ) : (
              <>
                <div className="text-xs text-zinc-500">Dată: <span className="font-semibold text-zinc-100">{local.date || "—"}</span></div>
                <div className="text-xs text-zinc-500">Curier: <span className="font-semibold text-zinc-100">{local.carrier || "—"}</span></div>
                <div className="text-xs text-zinc-500">AWB: <span className="font-mono font-semibold text-emerald-300">{local.awb || "—"}</span></div>
                <div className="text-xs text-zinc-500">Transport: <span className="font-mono font-semibold text-sky-300">{(+local.transport_price || 0).toFixed(2)} RON</span></div>
              </>
            )}
            <select value={local.status} onChange={(e) => changeStatus(e.target.value)}
              data-testid={`shipment-status-${shipment.id}`}
              className={`rounded border px-2 py-1 text-xs font-semibold outline-none ${meta.color}`}>
              {Object.entries(SHIPMENT_STATUS_META).map(([k, m]) => (
                <option key={k} value={k} className="bg-zinc-950 text-zinc-100">{m.label}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-1">
            {editing ? (
              <button onClick={saveHeader} data-testid={`shipment-save-${shipment.id}`}
                className="inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/10 px-2 py-1 text-xs text-emerald-200 hover:bg-emerald-500/20">
                <Save className="h-3 w-3" />Salvează
              </button>
            ) : (
              <button onClick={() => setEditing(true)} data-testid={`shipment-edit-${shipment.id}`}
                className="rounded border border-zinc-700 p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200">
                <Edit3 className="h-3 w-3" />
              </button>
            )}
            {shipment.has_invoice ? (
              <a href={invoiceUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 rounded border border-emerald-500/40 bg-emerald-500/5 px-2 py-1 text-xs text-emerald-300 hover:bg-emerald-500/10">
                <FileText className="h-3 w-3" />Factură
              </a>
            ) : (
              <>
                <button onClick={() => invoiceRef.current?.click()} data-testid={`shipment-upload-${shipment.id}`}
                  className="inline-flex items-center gap-1 rounded border border-zinc-700 px-2 py-1 text-xs text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200">
                  <Upload className="h-3 w-3" />Factură
                </button>
                <input ref={invoiceRef} type="file" accept="application/pdf,image/*" onChange={uploadInvoice} className="hidden" />
              </>
            )}
            <button onClick={() => onDelete(shipment.id)} data-testid={`shipment-delete-${shipment.id}`}
              className="rounded border border-rose-500/40 p-1.5 text-rose-300 hover:bg-rose-500/10">
              <Trash2 className="h-3 w-3" />
            </button>
          </div>
        </div>
        {editing && (
          <textarea placeholder="Note (opțional)" value={local.notes || ""} onChange={(e) => setLocal({ ...local, notes: e.target.value })}
            className="mt-2 w-full rounded border border-zinc-700 bg-zinc-900 px-2 py-1 text-xs text-zinc-100" rows="2" />
        )}
        {!editing && local.notes && <div className="mt-2 text-[11px] italic text-zinc-500">Note: {local.notes}</div>}
      </div>

      {/* Items */}
      <div className="p-4">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500">Produse expediate ({(local.items || []).length})</div>
          <div className="w-72">
            <ProductPicker value={null} onSelect={addItem} data-testid={`shipment-add-item-${shipment.id}`} />
          </div>
        </div>
        {(local.items || []).length === 0 ? (
          <div className="rounded border border-dashed border-zinc-800 p-4 text-center text-xs text-zinc-500">
            Niciun produs. Adaugă unul din dropdown-ul de sus.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500">
                <th className="p-2 text-left">SKU</th>
                <th className="p-2 text-left">Nume</th>
                <th className="p-2 text-left">Buc</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {(local.items || []).map((it) => (
                <ShipmentItemRow key={it.item_id} shipmentId={shipment.id} item={it}
                  onUpdate={updateItem} onDelete={deleteItem} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function RepairShipmentsTab() {
  const [shipments, setShipments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/repair-shipments`);
      setShipments(r.data?.rows || []);
    } catch (e) { toast.error("Eroare: " + (e.response?.data?.detail || e.message)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createShipment = async () => {
    const today = new Date().toISOString().slice(0, 10);
    setCreating(true);
    try {
      const r = await axios.post(`${API}/repair-shipments`, {
        date: today, transport_price: 0, awb: "", carrier: "", status: "creata",
      });
      setShipments((prev) => [r.data, ...prev]);
      toast.success("Cerere creată");
    } catch (e) { toast.error("Creare eșuată: " + (e.response?.data?.detail || e.message)); }
    finally { setCreating(false); }
  };
  const deleteShipment = async (id) => {
    if (!window.confirm("Șterg toată cererea?")) return;
    try {
      await axios.delete(`${API}/repair-shipments/${id}`);
      setShipments((prev) => prev.filter((s) => s.id !== id));
      toast.success("Șters");
    } catch (e) { toast.error("Ștergere eșuată: " + (e.response?.data?.detail || e.message)); }
  };
  const updateShipment = (updated) => setShipments((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-zinc-500">
          {shipments.length} {shipments.length === 1 ? "cerere" : "cereri"}
        </div>
        <button onClick={createShipment} disabled={creating}
          data-testid="new-shipment-btn"
          className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-medium text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-50">
          <Plus className="h-3.5 w-3.5" />
          {creating ? "Se creează..." : "Cerere nouă"}
        </button>
      </div>

      {loading && <div className="p-8 text-center text-xs text-zinc-500">Se încarcă...</div>}
      {!loading && shipments.length === 0 && (
        <div className="rounded-xl border border-dashed border-zinc-800 p-8 text-center text-xs text-zinc-500">
          <Wrench className="mx-auto mb-2 h-5 w-5" />
          Nicio cerere. Adaugă prima cerere.
        </div>
      )}
      {shipments.map((s) => (
        <ShipmentCard key={s.id} shipment={s} onUpdate={updateShipment} onDelete={deleteShipment} onRefresh={load} />
      ))}
    </div>
  );
}

// ─── Tab 3: Pași Listare Produs Nou ────────────────────────────────
// 26 predefined workflow steps for launching a new product. Users add a
// product by name, then tick off each step from a modal. Checked steps
// turn emerald; unchecked stay neutral. State persists in Mongo via
// /product-listing-checklists.

const LISTING_STEPS = [
  "Cerut în China Die Cut Cutie, Manual, negociat preț produs",
  "De cerut în China poze cu produsul și toate caracteristicile produsului",
  "De Generat EAN la GS1",
  "De făcut cod QR manual",
  "De dat la făcut cutie și main image Fiverr",
  "De făcut manual cu Claude AI",
  "De verificat cu atenție EAN, Cod produs, SKU pe cutie",
  "De cerut în China CE/MSDS",
  "De cerut în China informații produs: Greutate produs, Greutate cutie produs, Greutate plastic interior cutie, Greutate cutie mare transport, Greutate baterie (dacă are), buc/bax, dimensiuni bax, greutate bax",
  "De cerut preț Carrie manual + cutie",
  "De adăugat toate informațiile în platforma online Feishu",
  "De verificat cu atenție prețul din PI cu prețul în RMB",
  "De trimis toate info la Videnda pentru a face clasificarea",
  "De updatat tabele Videnda și făcut upload documente noi în platforma Bragus-app",
  "De făcut listare cu AI pentru produs: RO/BG/HU",
  "De listat pe site (pus în descriere taxele de reciclare)",
  "De adăugat produsul în platforma Bragus-app",
  "De completat toate datele în platforma Bragus-app",
  "De adăugat produsul in Calcul Preț și făcut un preț estimativ",
  "De adăugat descriere în Oblio cu tot cu produs",
  "De listat pe Emag RO BAU",
  "De facut link EMAG RO BAU - Bragus App",
  "De adăugat în FBE RO",
  "De făcut link FBE RO Bragus-app",
  "De sincronizat stoc FBE în Bragus-app",
  "De listat pe Emag BG",
  "De făcut link între FBE BG și Bragus App",
  "De adăugat taxă reciclare Emag RO si FBE",
  "De calculat din nou pret exact cand vine produsul",
  "De setat pret corect pe toate platformele din Bragus App",
  "De pus reviews",
  "De trimis în FBE",
  "De spus Ramona când e în stoc FBE pentru Ads",
  "De făcut link în mesagerie cu FBE BG dacă nu e corect ID",
  "De listat pe Trendyol",
];

function ListingChecklistTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [openId, setOpenId] = useState(null);
  const [stepLinks, setStepLinks] = useState({});   // {"3": "https://docs..."} — global per step

  const loadLinks = async () => {
    try {
      const r = await axios.get(`${API}/listing-step-links`);
      setStepLinks(r.data?.links || {});
    } catch (e) { /* ignore — non-critical */ }
  };
  useEffect(() => { loadLinks(); }, []);

  const saveStepLink = async (stepIdx, url) => {
    try {
      const r = await axios.patch(`${API}/listing-step-links`, { step_idx: stepIdx, url });
      setStepLinks(r.data?.links || {});
      toast.success(url ? "Link salvat" : "Link șters");
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleStepLinkClick = (idx) => {
    const cur = stepLinks[String(idx)];
    if (cur) {
      // Internal path starting with "/" — navigate within app; external → new tab
      if (cur.startsWith("/")) {
        window.open(cur, "_blank");
      } else {
        window.open(cur, "_blank", "noopener,noreferrer");
      }
    } else {
      const url = window.prompt("Adaugă un link pentru acest pas (Google Docs URL sau /cale-internă):");
      if (url && url.trim()) saveStepLink(idx, url.trim());
    }
  };

  const handleStepLinkEdit = (e, idx) => {
    e.preventDefault();
    e.stopPropagation();
    const cur = stepLinks[String(idx)] || "";
    const url = window.prompt("Editează link-ul (lasă gol ca să ștergi):", cur);
    if (url === null) return;   // canceled
    saveStepLink(idx, url.trim());
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/product-listing-checklists`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Eroare încărcare: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addProduct = async () => {
    const n = name.trim();
    if (!n) return;
    try {
      await axios.post(`${API}/product-listing-checklists`, { name: n });
      setName("");
      await load();
      toast.success("Produs adăugat");
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Ștergi produsul din listă? Progresul se pierde.")) return;
    try {
      await axios.delete(`${API}/product-listing-checklists/${id}`);
      await load();
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    }
  };

  const toggleStep = async (item, stepIdx) => {
    const completed = new Set(item.completed_steps || []);
    if (completed.has(stepIdx)) completed.delete(stepIdx);
    else completed.add(stepIdx);
    const arr = Array.from(completed).sort((a, b) => a - b);
    // Optimistic update
    setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, completed_steps: arr } : x)));
    try {
      await axios.patch(`${API}/product-listing-checklists/${item.id}`, { completed_steps: arr });
    } catch (e) {
      toast.error("Eroare salvare: " + (e.response?.data?.detail || e.message));
      await load(); // rollback
    }
  };

  const openItem = items.find((x) => x.id === openId) || null;

  return (
    <div data-testid="listing-checklist-tab">
      {/* Add product form */}
      <div className="mb-4 flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 p-3">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") addProduct(); }}
          placeholder='Nume produs nou (ex: „Lampă LED Solar 2026")'
          data-testid="listing-checklist-name-input"
          className="flex-1 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 placeholder-zinc-600 outline-none focus:border-emerald-600"
        />
        <button
          onClick={addProduct}
          disabled={!name.trim()}
          data-testid="listing-checklist-add-btn"
          className="inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-500 disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" /> Adaugă produs
        </button>
      </div>

      {/* Product list */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950">
        {loading && <div className="p-4 text-center text-xs text-zinc-500">Se încarcă…</div>}
        {!loading && items.length === 0 && (
          <div className="p-6 text-center text-sm text-zinc-500">
            Nicio listare încă. Adaugă primul produs mai sus.
          </div>
        )}
        {!loading && items.map((it) => {
          const done = (it.completed_steps || []).length;
          const total = LISTING_STEPS.length;
          const pct = Math.round((done / total) * 100);
          const isDone = done === total;
          return (
            <div key={it.id}
              className="flex items-center gap-3 border-b border-zinc-900 px-4 py-3 last:border-b-0 hover:bg-zinc-900/50"
              data-testid={`listing-checklist-row-${it.id}`}>
              <button
                onClick={() => setOpenId(it.id)}
                data-testid={`listing-checklist-open-${it.id}`}
                className={`flex-1 text-left text-sm font-medium ${isDone ? "text-emerald-400" : "text-zinc-100"} hover:underline`}
              >
                {it.name}
              </button>
              <div className="flex items-center gap-2">
                <div className="h-1.5 w-32 overflow-hidden rounded-full bg-zinc-800">
                  <div
                    className={`h-full ${isDone ? "bg-emerald-500" : "bg-emerald-600/70"}`}
                    style={{ width: `${pct}%` }}
                  />
                </div>
                <span className={`w-14 text-right text-xs tabular-nums ${isDone ? "text-emerald-400" : "text-zinc-400"}`}>
                  {done}/{total}
                </span>
              </div>
              <button
                onClick={() => deleteProduct(it.id)}
                data-testid={`listing-checklist-delete-${it.id}`}
                className="rounded p-1.5 text-zinc-500 hover:bg-red-900/30 hover:text-red-400"
                title="Șterge"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          );
        })}
      </div>

      {/* Steps modal */}
      {openItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setOpenId(null)}>
          <div
            className="max-h-[90vh] w-full max-w-2xl overflow-hidden rounded-xl border border-zinc-800 bg-zinc-950 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="listing-checklist-modal"
          >
            <div className="flex items-center justify-between border-b border-zinc-800 bg-zinc-900 px-5 py-3">
              <div>
                <h2 className="text-sm font-semibold text-zinc-100">{openItem.name}</h2>
                <p className="text-[11px] text-zinc-500">
                  {(openItem.completed_steps || []).length}/{LISTING_STEPS.length} pași completați
                </p>
              </div>
              <button
                onClick={() => setOpenId(null)}
                data-testid="listing-checklist-modal-close"
                className="rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="max-h-[75vh] overflow-y-auto p-4">
              <ol className="space-y-2">
                {LISTING_STEPS.map((label, idx) => {
                  const checked = (openItem.completed_steps || []).includes(idx);
                  return (
                    <li key={idx}>
                      <label
                        className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition ${
                          checked
                            ? "border-emerald-600/60 bg-emerald-950/40"
                            : "border-zinc-800 bg-zinc-900 hover:border-zinc-700"
                        }`}
                        data-testid={`listing-step-${idx}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleStep(openItem, idx)}
                          data-testid={`listing-step-checkbox-${idx}`}
                          className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-zinc-600 bg-zinc-950 text-emerald-500 focus:ring-emerald-500"
                        />
                        <span className={`text-sm ${checked ? "text-emerald-300 line-through decoration-emerald-700/60" : "text-zinc-100"}`}>
                          <span className={`mr-2 font-mono text-xs ${checked ? "text-emerald-400" : "text-zinc-500"}`}>
                            {String(idx + 1).padStart(2, "0")}.
                          </span>
                          {label}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Tab 4: Calcul Preț Produse ─────────────────────────────────────
// Replica funcțională a tab 2 din spreadsheet-ul intern (34 coloane).
// Backend stochează DOAR input-urile manuale (12 câmpuri). Toate derivatele
// se calculează pe client în timp real din formulele originale ale sheet-ului.

const PC_VAT_RATE = 0.21;
const PC_EMAG_ADS_PCT = 0.10;
const PC_ACCESSORIES_PCT = 0.01;
const PC_GENIUS_FIXED_RON = 8;  // Comision Genius: constant 8 lei/unit.

function pcDeriv(row, s) {
  const b = +row.weight_air || 0;
  const c = +row.volume_sea || 0;
  const d = +row.price_usd || 0;
  const j = +row.customs_tax || 0;
  const o = +row.sale_price || 0;
  const u = +row.fbe_cost || 0;
  const v = +row.gift || 0;
  const w = +row.green_tax || 0;
  const x = 0;  // Bat+Amb+Cart removed per user (2026-07-15).
  const z = PC_GENIUS_FIXED_RON;  // fixed 8 lei/unit
  const ai = +row.import_qty || 0;
  const usdRon = +s.usd_to_ron || 4.4;
  const airPerKg = +s.transport_air_per_kg || 0;
  const seaPerCbm = +s.transport_sea_per_cbm || 0;
  // Comision eMag: per-product percentage (0-100) from product info.
  // Fall back to legacy 21% when not set so existing rows keep their totals.
  const emagCommPctVal = row.emag_commission_pct;
  const commPct = (emagCommPctVal === undefined || emagCommPctVal === null || emagCommPctVal === "")
    ? PC_VAT_RATE
    : (Number(emagCommPctVal) / 100);
  const e = d * usdRon;
  const f = seaPerCbm * c * usdRon;  // seaPerCbm este USD/mc → convertim la RON
  const g = airPerKg * b * usdRon;   // airPerKg este USD/kg → convertim la RON
  const h = e + f;
  const i = e + g;
  const k = PC_VAT_RATE * h * j;
  const l = PC_VAT_RATE * i * j;
  const m = h + k;
  const n = i + l;
  const p = o / (1 + PC_VAT_RATE);
  const q = commPct * p;
  const r = PC_VAT_RATE * q;
  const sTot = q + r;
  const t = PC_EMAG_ADS_PCT * o;
  const y = PC_ACCESSORIES_PCT * e;
  const aa = o - m - sTot - t - u - z - v - y - w - x;
  const ab = o - n - sTot - t - u - z - y - v - w - x;
  const ac = m > 0 ? aa / m : 0;
  const ad = n > 0 ? ab / n : 0;
  const ae = p - h - q - t - u - v - w - x - y - z;
  const af = p - i - q - t - u - v - w - x - y - z;
  const ag = h > 0 ? ae / h : 0;
  // const ah = n > 0 ? af / n : 0;
  const ah = i > 0 ? af / i : 0;
  const aj = ae * ai;
  //const ak = ab * ai;
  const ak = af * ai;
  return { e, f, g, h, i, k, l, m, n, p, q, r, s: sTot, t, y, z, aa, ab, ac, ad, ae, af, ag, ah, aj, ak };
}

const pcNf = (v, dec = 2) => (v == null || isNaN(v) ? "—" : Number(v).toLocaleString("ro-RO", { minimumFractionDigits: dec, maximumFractionDigits: dec }));
const pcPf = (v) => (v == null || isNaN(v) ? "—" : (Number(v) * 100).toFixed(1) + "%");

function usePcDebouncedSave(saveFn, delay = 500) {
  const timer = React.useRef(null);
  return React.useCallback((payload) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => saveFn(payload), delay);
  }, [saveFn, delay]);
}

function PriceCalcTab() {
  const [items, setItems] = useState([]);
  const [settings, setSettings] = useState({ transport_air_per_kg: 38.25, transport_sea_per_cbm: 1014, usd_to_ron: 4.4 });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [newName, setNewName] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API}/price-calc/settings`),
        axios.get(`${API}/price-calc`),
      ]);
      setSettings(s.data?.settings || {});
      setSettingsLoaded(true);
      setItems(l.data?.items || []);
    } catch (e) {
      toast.error("Eroare încărcare: " + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async (patch) => {
    try {
      const r = await axios.put(`${API}/price-calc/settings`, patch);
      setSettings(r.data?.settings || {});
      toast.success("Setări salvate", { duration: 1500 });
    } catch (e) {
      toast.error("Eroare salvare setări: " + (e.response?.data?.detail || e.message));
    }
  };
  const dSaveSettings = usePcDebouncedSave(saveSettings, 600);

  const addProduct = async () => {
    const n = newName.trim();
    if (!n) return;
    try {
      const r = await axios.post(`${API}/price-calc`, { name: n });
      setItems((prev) => [r.data.item, ...prev]);
      setNewName("");
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    }
  };

  // Existing-product picker state
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState("");
  const [pickerResults, setPickerResults] = useState([]);  const [pickerLoading, setPickerLoading] = useState(false);
  useEffect(() => {
    if (!pickerOpen) return;
    const term = pickerQuery.trim();
    if (term.length < 2) { setPickerResults([]); return; }
    const t = setTimeout(async () => {
      setPickerLoading(true);
      try {
        const r = await axios.get(`${API}/price-calc/search-products`, { params: { q: term, limit: 15 } });
        setPickerResults(r.data?.items || []);
      } catch (e) {
        toast.error("Eroare căutare: " + (e.response?.data?.detail || e.message));
      } finally {
        setPickerLoading(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [pickerQuery, pickerOpen]);

  const addFromExisting = async (product) => {
    try {
      const pf = product.prefill || {};
      const payload = {
        name: product.name || product.sku,
        sku: product.sku,
        // `customs_tax` is a MULTIPLIER (1.00, 1.02 …). We must NOT strip the
        // 1.0 baseline via `|| 0` when the underlying % is 0.
        customs_tax: pf.customs_tax != null ? pf.customs_tax : 1.0,
        fbe_cost: pf.fbe_cost || 0,
        green_tax: pf.green_tax || 0,
        emag_commission_pct: pf.emag_commission_pct || 0,
        volume_sea: pf.volume_sea || 0,
        weight_air: pf.weight_air || 0,       // auto: max(vol, phys) / bucati_per_bax
        price_usd: pf.price_usd || pf.unit_price_usd || 0,
      };
      const r = await axios.post(`${API}/price-calc`, payload);
      setItems((prev) => [r.data.item, ...prev]);
      setPickerOpen(false);
      setPickerQuery("");
      setPickerResults([]);
      toast.success(`Adăugat: ${product.sku} — date pre-completate din informații produs.`);
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    }
  };

  const [refreshingRow, setRefreshingRow] = useState(null);
  const [hideAer, setHideAer] = useState(false);
  const [hideMare, setHideMare] = useState(false);
  const refreshFromProduct = async (id) => {
    setRefreshingRow(id);
    try {
      const r = await axios.post(`${API}/price-calc/${id}/refresh`);
      setItems((prev) => prev.map((x) => (x.id === id ? r.data.item : x)));
      toast.success(`Actualizat din produs: ${r.data.refreshed_from_sku}`);
    } catch (e) {
      toast.error("Actualizare eșuată: " + (e.response?.data?.detail || e.message));
    } finally {
      setRefreshingRow(null);
    }
  };

  const saveItem = async (payload) => {
    try {
      const r = await axios.patch(`${API}/price-calc/${payload.id}`, payload.patch);
      setItems((prev) => prev.map((x) => (x.id === payload.id ? r.data.item : x)));
    } catch (e) {
      toast.error("Eroare salvare: " + (e.response?.data?.detail || e.message));
    }
  };
  const dSaveItem = usePcDebouncedSave(saveItem, 500);

  const patchLocal = (id, key, value) => {
    setItems((prev) => prev.map((x) => (x.id === id ? { ...x, [key]: value } : x)));
    dSaveItem({ id, patch: { [key]: value } });
  };

  const deleteItem = async (id) => {
    if (!window.confirm("Ștergi produsul din calcul?")) return;
    try {
      await axios.delete(`${API}/price-calc/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
    } catch (e) {
      toast.error("Eroare: " + (e.response?.data?.detail || e.message));
    }
  };

  const clsMareBd = "bg-blue-950/40 border-l border-blue-900/60";
  const clsInp = "w-full rounded-sm bg-zinc-900 border border-zinc-700 px-1.5 py-1 text-xs text-zinc-100 focus:border-emerald-500 focus:outline-none";
  const clsInpMare = "w-full rounded-sm bg-blue-950/60 border border-blue-800 px-1.5 py-1 text-xs text-blue-100 focus:border-blue-400 focus:outline-none";

  return (
    <div data-testid="price-calc-tab">
      <div className="mb-4 grid grid-cols-1 gap-3 rounded-lg border border-zinc-800 bg-zinc-950 p-3 md:grid-cols-4">
        <div className="rounded-md bg-zinc-900 p-3">
          <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Preț transport per kg (Aer) — AM3</label>
          <div className="flex items-center gap-2">
            <input key={`air-${settingsLoaded}`} type="number" step="0.01" min="0" defaultValue={settings.transport_air_per_kg} onChange={(e) => dSaveSettings({ transport_air_per_kg: e.target.value })} data-testid="pc-settings-air"
              className="w-32 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none" />
            <span className="text-xs text-zinc-500">USD/kg</span>
          </div>
        </div>
        <div className="rounded-md bg-blue-950/50 p-3">
          <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-blue-300">Preț transport per mc (Mare) — AM4</label>
          <div className="flex items-center gap-2">
            <input key={`sea-${settingsLoaded}`} type="number" step="0.01" min="0" defaultValue={settings.transport_sea_per_cbm} onChange={(e) => dSaveSettings({ transport_sea_per_cbm: e.target.value })} data-testid="pc-settings-sea"
              className="w-32 rounded-md border border-blue-700 bg-blue-950 px-2 py-1.5 text-sm text-blue-100 focus:border-blue-400 focus:outline-none" />
            <span className="text-xs text-blue-300">USD/mc</span>
          </div>
        </div>
        <div className="rounded-md bg-zinc-900 p-3">
          <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-zinc-500">Curs USD → RON</label>
          <div className="flex items-center gap-2">
            <input key={`usd-${settingsLoaded}`} type="number" step="0.001" min="0" defaultValue={settings.usd_to_ron} onChange={(e) => dSaveSettings({ usd_to_ron: e.target.value })} data-testid="pc-settings-usd-ron"
              className="w-32 rounded-md border border-zinc-700 bg-zinc-950 px-2 py-1.5 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none" />
            <span className="text-xs text-zinc-500">RON/USD</span>
          </div>
        </div>
        <div className="rounded-md bg-amber-950/40 p-3">
          <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-amber-300">Curs USD → RMB (China)</label>
          <div className="flex items-center gap-2">
            <input key={`rmb-${settingsLoaded}`} type="number" step="0.001" min="0" defaultValue={settings.usd_to_rmb ?? 7.2} onChange={(e) => dSaveSettings({ usd_to_rmb: e.target.value })} data-testid="pc-settings-usd-rmb"
              className="w-32 rounded-md border border-amber-700 bg-amber-950 px-2 py-1.5 text-sm text-amber-100 focus:border-amber-400 focus:outline-none" />
            <span className="text-xs text-amber-300">RMB/USD</span>
          </div>
        </div>
      </div>

      <div className="mb-3 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => setPickerOpen((v) => !v)} data-testid="pc-add-existing-btn"
            className={`inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold text-white ${pickerOpen ? "bg-emerald-500" : "bg-emerald-600 hover:bg-emerald-500"}`}>
            <Search className="h-3.5 w-3.5" /> Adaugă produs existent
          </button>
          <input value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") addProduct(); }}
            placeholder="Sau: nume produs nou…" data-testid="pc-new-name"
            className="flex-1 min-w-[200px] rounded-md border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-600 focus:outline-none" />
          <button onClick={addProduct} disabled={!newName.trim()} data-testid="pc-add-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-zinc-800 border border-zinc-700 px-3 py-2 text-xs font-semibold text-zinc-100 hover:border-emerald-600 hover:bg-zinc-700 disabled:opacity-40">
            <Plus className="h-3.5 w-3.5" /> Adaugă produs nou
          </button>
        </div>

        {pickerOpen && (
          <div className="rounded-lg border border-emerald-600/40 bg-zinc-950 p-3" data-testid="pc-picker-panel">
            <div className="mb-2 flex items-center gap-2">
              <Search className="h-4 w-4 text-zinc-500" />
              <input autoFocus value={pickerQuery} onChange={(e) => setPickerQuery(e.target.value)}
                placeholder="Caută după SKU sau nume produs (min. 2 caractere)…" data-testid="pc-picker-search"
                className="flex-1 rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 focus:border-emerald-500 focus:outline-none" />
              <button onClick={() => { setPickerOpen(false); setPickerQuery(""); setPickerResults([]); }} className="text-zinc-500 hover:text-zinc-200" data-testid="pc-picker-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            {pickerLoading && <div className="py-2 text-center text-xs text-zinc-500">Se caută…</div>}
            {!pickerLoading && pickerQuery.trim().length >= 2 && pickerResults.length === 0 && (
              <div className="py-2 text-center text-xs text-zinc-500">Niciun produs găsit.</div>
            )}
            {pickerResults.length > 0 && (
              <ul className="max-h-72 overflow-y-auto divide-y divide-zinc-800" data-testid="pc-picker-results">
                {pickerResults.map((p) => (
                  <li key={p.sku} className="flex items-center gap-3 px-2 py-2 hover:bg-zinc-900">
                    {p.image ? <img src={p.image} alt="" className="h-10 w-10 rounded object-cover" /> : <div className="h-10 w-10 rounded bg-zinc-800" />}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-[11px]">
                        <span className="font-mono font-semibold text-emerald-300">{p.sku}</span>
                        <span className="truncate text-zinc-200">{p.name}</span>
                      </div>
                      <div className="mt-0.5 text-[10px] text-zinc-500 space-x-3">
                        <span>Vamă: <span className="text-zinc-300">{p.prefill?.customs_tax_pct_raw ?? 0}%</span></span>
                        <span>Com eMag: <span className="text-zinc-300">{p.prefill?.emag_commission_pct || 0}%</span></span>
                        <span>Com FBE: <span className="text-zinc-300">{(p.prefill?.fbe_cost || 0).toFixed(2)} RON</span></span>
                        <span>Reciclare: <span className="text-zinc-300">{(p.prefill?.green_tax || 0).toFixed(2)} RON</span></span>
                        <span>Vol/buc: <span className="text-zinc-300">{(p.prefill?.volume_sea || 0).toFixed(5)} m³</span></span>
                        <span>G aer/buc: <span className="text-zinc-300">{(p.prefill?.weight_air || 0).toFixed(3)} kg</span></span>
                        <span>USD China: <span className="text-zinc-300">${(p.prefill?.price_usd || 0).toFixed(2)}</span>{p.prefill?.price_usd_source === "computed_from_rmb" && <span className="ml-1 rounded bg-amber-950/60 px-1 text-[9px] text-amber-300">auto RMB</span>}</span>
                      </div>
                    </div>
                    <button onClick={() => addFromExisting(p)} data-testid={`pc-picker-add-${p.sku}`}
                      className="shrink-0 inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-emerald-500">
                      <Plus className="h-3 w-3" /> Adaugă
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <button
          onClick={() => setHideAer((v) => !v)}
          data-testid="pc-toggle-hide-aer"
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold border transition-colors ${
            hideAer
              ? "bg-emerald-600 text-white border-emerald-500 hover:bg-emerald-500"
              : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:border-emerald-600 hover:text-emerald-300"
          }`}
          title={hideAer ? "Reafișează coloanele Aer" : "Ascunde toate coloanele care conțin cuvântul Aer"}
        >
          {hideAer ? "Arată Aer" : "Ascunde Aer"}
        </button>
        <button
          onClick={() => setHideMare((v) => !v)}
          data-testid="pc-toggle-hide-mare"
          className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold border transition-colors ${
            hideMare
              ? "bg-blue-600 text-white border-blue-500 hover:bg-blue-500"
              : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:border-blue-600 hover:text-blue-300"
          }`}
          title={hideMare ? "Reafișează coloanele Mare" : "Ascunde toate coloanele care conțin cuvântul Mare"}
        >
          {hideMare ? "Arată Mare" : "Ascunde Mare"}
        </button>
      </div>

      <div className="rounded-lg border border-zinc-800 bg-zinc-950">
        {loading && <div className="p-4 text-center text-xs text-zinc-500">Se încarcă…</div>}
        {!loading && items.length === 0 && (
          <div className="p-6 text-center text-sm text-zinc-500">Niciun produs. Adaugă unul mai sus.</div>
        )}
        {!loading && items.length > 0 && (
          <div className="overflow-x-auto">
            <table className={`min-w-full text-[11px] tabular-nums ${hideAer ? "[&_.col-aer]:hidden" : ""} ${hideMare ? "[&_.col-mare]:hidden" : ""}`} data-testid="pc-table">
              <thead className="sticky top-0 z-10 bg-zinc-900 text-zinc-400">
                <tr>
                  <th className="whitespace-nowrap px-2 py-2 text-left font-semibold">SKU</th>
                  <th className="whitespace-nowrap px-2 py-2 text-left font-semibold">Nume Produs</th>
                  <th className="col-aer whitespace-nowrap px-2 py-2 text-right">Greutate/buc Aer (kg)</th>
                  <th className={`col-mare ${clsMareBd} whitespace-nowrap px-2 py-2 text-right`}>Greutate/buc Mare (mc)</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right border-l border-zinc-800">Preț USD China</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">Preț per buc LEI</th>
                  <th className={`col-mare ${clsMareBd} whitespace-nowrap px-2 py-2 text-right`}>Preț transp Mare</th>
                  <th className="col-aer whitespace-nowrap px-2 py-2 text-right">Preț transp Aer</th>
                  <th className={`col-mare ${clsMareBd} whitespace-nowrap px-2 py-2 text-right`}>Total Fără Vamă Mare</th>
                  <th className="col-aer whitespace-nowrap px-2 py-2 text-right">Total Fără Vamă Aer</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right border-l border-zinc-800">Taxa Vamală</th>
                  <th className={`col-mare ${clsMareBd} whitespace-nowrap px-2 py-2 text-right`}>TVA+Taxe Mare</th>
                  <th className="col-aer whitespace-nowrap px-2 py-2 text-right">TVA+Taxe Aer</th>
                  <th className={`col-mare ${clsMareBd} whitespace-nowrap px-2 py-2 text-right`}>Preț total Mare</th>
                  <th className="col-aer whitespace-nowrap px-2 py-2 text-right">Preț total Aer</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right border-l border-zinc-800">Preț Vânzare TVA inclus</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">Preț Vânzare Fără TVA</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">% Com eMag</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">Com eMag</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">TVA Com eMag</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">Total Com eMag</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">eMag Ads</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right border-l border-zinc-800">Cost FBE</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">Cadou</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">Taxa Verde</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">Accesorii</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right">Com Genius</th>
                  <th className={`col-mare ${clsMareBd} whitespace-nowrap px-2 py-2 text-right`}>Profit Mare +TVA</th>
                  <th className="col-aer whitespace-nowrap px-2 py-2 text-right">Profit Aer +TVA</th>
                  <th className={`col-mare ${clsMareBd} whitespace-nowrap px-2 py-2 text-right`}>% Mare +TVA</th>
                  <th className="col-aer whitespace-nowrap px-2 py-2 text-right">% Aer +TVA</th>
                  <th className={`col-mare ${clsMareBd} whitespace-nowrap px-2 py-2 text-right`}>Profit Mare -TVA</th>
                  <th className="col-aer whitespace-nowrap px-2 py-2 text-right">Profit Aer -TVA</th>
                  <th className={`col-mare ${clsMareBd} whitespace-nowrap px-2 py-2 text-right`}>% Mare -TVA</th>
                  <th className="col-aer whitespace-nowrap px-2 py-2 text-right">% Aer -TVA</th>
                  <th className="whitespace-nowrap px-2 py-2 text-right border-l border-zinc-800">Nr buc import</th>
                  <th className={`col-mare ${clsMareBd} whitespace-nowrap px-2 py-2 text-right`}>Profit Total Mare</th>
                  <th className="col-aer whitespace-nowrap px-2 py-2 text-right">Profit Total Aer</th>
                  <th className="w-8"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const d = pcDeriv(row, settings);
                  return (
                    <tr key={row.id} className="border-t border-zinc-900" data-testid={`pc-row-${row.id}`}>
                      <td className="px-2 py-1 min-w-[90px]">
                        {row.sku
                          ? <span data-testid={`pc-sku-${row.id}`} className="inline-block rounded bg-emerald-950/40 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-emerald-300">{row.sku}</span>
                          : <span className="text-[10px] text-zinc-600">manual</span>}
                      </td>
                      <td className="px-2 py-1 min-w-[140px]"><input defaultValue={row.name} onChange={(e) => patchLocal(row.id, "name", e.target.value)} data-testid={`pc-name-${row.id}`} className={clsInp} /></td>
                      <td className="col-aer px-2 py-1 text-right"><input type="number" step="0.0001" defaultValue={row.weight_air || ""} onChange={(e) => patchLocal(row.id, "weight_air", e.target.value)} className={`${clsInp} text-right`} /></td>
                      <td className={`col-mare ${clsMareBd} px-2 py-1 text-right`}><input type="number" step="0.0001" defaultValue={row.volume_sea || ""} onChange={(e) => patchLocal(row.id, "volume_sea", e.target.value)} className={`${clsInpMare} text-right`} /></td>
                      <td className="px-2 py-1 text-right border-l border-zinc-800"><input type="number" step="0.01" defaultValue={row.price_usd || ""} onChange={(e) => patchLocal(row.id, "price_usd", e.target.value)} className={`${clsInp} text-right`} /></td>
                      <td className="px-2 py-1 text-right text-zinc-400">{pcNf(d.e)}</td>
                      <td className={`col-mare ${clsMareBd} px-2 py-1 text-right text-blue-200`}>{pcNf(d.f)}</td>
                      <td className="col-aer px-2 py-1 text-right text-zinc-400">{pcNf(d.g)}</td>
                      <td className={`col-mare ${clsMareBd} px-2 py-1 text-right text-blue-200`}>{pcNf(d.h)}</td>
                      <td className="col-aer px-2 py-1 text-right text-zinc-400">{pcNf(d.i)}</td>
                      <td className="px-2 py-1 text-right border-l border-zinc-800"><input type="number" step="0.01" defaultValue={row.customs_tax || ""} onChange={(e) => patchLocal(row.id, "customs_tax", e.target.value)} className={`${clsInp} text-right`} /></td>
                      <td className={`col-mare ${clsMareBd} px-2 py-1 text-right text-blue-200`}>{pcNf(d.k)}</td>
                      <td className="col-aer px-2 py-1 text-right text-zinc-400">{pcNf(d.l)}</td>
                      <td className={`col-mare ${clsMareBd} px-2 py-1 text-right text-blue-100 font-semibold`}>{pcNf(d.m)}</td>
                      <td className="col-aer px-2 py-1 text-right text-zinc-200 font-semibold">{pcNf(d.n)}</td>
                      <td className="px-2 py-1 text-right border-l border-zinc-800"><input type="number" step="0.01" defaultValue={row.sale_price || ""} onChange={(e) => patchLocal(row.id, "sale_price", e.target.value)} className={`${clsInp} text-right`} /></td>
                      <td className="px-2 py-1 text-right text-zinc-400">{pcNf(d.p)}</td>
                      <td className="px-2 py-1 text-right"><input type="number" step="0.01" min="0" defaultValue={row.emag_commission_pct || ""} onChange={(e) => patchLocal(row.id, "emag_commission_pct", e.target.value)} data-testid={`pc-emag-pct-${row.id}`} className={`${clsInp} text-right`} placeholder="21" /></td>
                      <td className="px-2 py-1 text-right text-zinc-400">{pcNf(d.q)}</td>
                      <td className="px-2 py-1 text-right text-zinc-400">{pcNf(d.r)}</td>
                      <td className="px-2 py-1 text-right text-zinc-400">{pcNf(d.s)}</td>
                      <td className="px-2 py-1 text-right text-zinc-400">{pcNf(d.t)}</td>
                      <td className="px-2 py-1 text-right border-l border-zinc-800"><input type="number" step="0.01" defaultValue={row.fbe_cost || ""} onChange={(e) => patchLocal(row.id, "fbe_cost", e.target.value)} className={`${clsInp} text-right`} /></td>
                      <td className="px-2 py-1 text-right"><input type="number" step="0.01" defaultValue={row.gift || ""} onChange={(e) => patchLocal(row.id, "gift", e.target.value)} className={`${clsInp} text-right`} /></td>
                      <td className="px-2 py-1 text-right"><input type="number" step="0.01" defaultValue={row.green_tax || ""} onChange={(e) => patchLocal(row.id, "green_tax", e.target.value)} className={`${clsInp} text-right`} /></td>
                      <td className="px-2 py-1 text-right text-zinc-400">{pcNf(d.y)}</td>
                      <td className="px-2 py-1 text-right text-zinc-500" data-testid={`pc-genius-${row.id}`}>{pcNf(d.z)}</td>
                      <td className={`col-mare ${clsMareBd} px-2 py-1 text-right ${d.aa >= 0 ? "text-emerald-300" : "text-red-300"} font-semibold`}>{pcNf(d.aa)}</td>
                      <td className={`col-aer px-2 py-1 text-right ${d.ab >= 0 ? "text-emerald-300" : "text-red-300"} font-semibold`}>{pcNf(d.ab)}</td>
                      <td className={`col-mare ${clsMareBd} px-2 py-1 text-right ${d.ac >= 0 ? "text-emerald-300" : "text-red-300"}`}>{pcPf(d.ac)}</td>
                      <td className={`col-aer px-2 py-1 text-right ${d.ad >= 0 ? "text-emerald-300" : "text-red-300"}`}>{pcPf(d.ad)}</td>
                      <td className={`col-mare ${clsMareBd} px-2 py-1 text-right ${d.ae >= 0 ? "text-emerald-300" : "text-red-300"} font-semibold`}>{pcNf(d.ae)}</td>
                      <td className={`col-aer px-2 py-1 text-right ${d.af >= 0 ? "text-emerald-300" : "text-red-300"} font-semibold`}>{pcNf(d.af)}</td>
                      <td className={`col-mare ${clsMareBd} px-2 py-1 text-right ${d.ag >= 0 ? "text-emerald-300" : "text-red-300"}`}>{pcPf(d.ag)}</td>
                      <td className={`col-aer px-2 py-1 text-right ${d.ah >= 0 ? "text-emerald-300" : "text-red-300"}`}>{pcPf(d.ah)}</td>
                      <td className="px-2 py-1 text-right border-l border-zinc-800"><input type="number" step="1" defaultValue={row.import_qty || ""} onChange={(e) => patchLocal(row.id, "import_qty", e.target.value)} className={`${clsInp} text-right`} /></td>
                      <td className={`col-mare ${clsMareBd} px-2 py-1 text-right ${d.aj >= 0 ? "text-emerald-300" : "text-red-300"} font-bold`}>{pcNf(d.aj, 0)}</td>
                      <td className={`col-aer px-2 py-1 text-right ${d.ak >= 0 ? "text-emerald-300" : "text-red-300"} font-bold`}>{pcNf(d.ak, 0)}</td>
                      <td className="px-1 text-center">
                        <div className="inline-flex items-center gap-0.5">
                          {row.sku && (
                            <button
                              onClick={() => refreshFromProduct(row.id)}
                              disabled={refreshingRow === row.id}
                              data-testid={`pc-refresh-${row.id}`}
                              className={`rounded p-1 text-zinc-500 hover:bg-emerald-900/30 hover:text-emerald-300 disabled:opacity-50 ${refreshingRow === row.id ? "animate-spin text-emerald-300" : ""}`}
                              title={`Actualizează din pagina Produse (${row.sku})`}
                            >
                              <RefreshCw className="h-3 w-3" />
                            </button>
                          )}
                          <button onClick={() => deleteItem(row.id)} data-testid={`pc-delete-${row.id}`} className="rounded p-1 text-zinc-600 hover:bg-red-900/30 hover:text-red-400" title="Șterge">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="mt-3 text-[11px] text-zinc-500">
        <span className="font-semibold text-zinc-400">Legendă:</span> celulele cu fundal <span className="rounded bg-blue-950/60 px-1 text-blue-200">albastru</span> = transport Mare; fundal neutru = Aer. Input-urile cu border evidențiat sunt manuale — restul se calculează automat. Salvare automată la ~500ms după ultima tastă.
      </p>
    </div>
  );
}

// ─── Page shell w/ tabs ────────────────────────────────────────────
const TABS = [
  { key: "weight", label: "Calcul greutate transport", icon: Calculator },
  { key: "repair", label: "Trimise pentru reparație/înlocuire", icon: Wrench },
  { key: "listing", label: "Pași Listare Produs Nou", icon: FileText },
  { key: "price", label: "Calcul Preț Produse", icon: Coins },
];

export default function Calculations() {
  const [tab, setTab] = useState("weight");
  return (
    <div className="mx-auto max-w-[1500px] p-6" data-testid="calculations-page">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-zinc-100">Calcule & Status listări</h1>
          <p className="text-xs text-zinc-500">Instrumente rapide pentru calcule operaționale.</p>
        </div>
      </div>

      <div className="mb-4 flex gap-1 border-b border-zinc-800">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-2 text-xs font-medium transition ${
                active ? "border-emerald-500 text-emerald-300" : "border-transparent text-zinc-400 hover:text-zinc-200"
              }`}>
              <Icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {tab === "weight" && <WeightTransportTab />}
      {tab === "repair" && <RepairShipmentsTab />}
      {tab === "listing" && <ListingChecklistTab />}
      {tab === "price" && <PriceCalcTab />}
    </div>
  );
}
